import { useEffect, useRef, useState } from "react";
import { _registerConfirmHandler } from "@/lib/confirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, HelpCircle, KeyRound } from "lucide-react";

/**
 * ConfirmDialogHost — single, app-wide themed replacement for the
 * browser's ``window.confirm`` / ``window.prompt`` primitives.
 *
 * Mounted once from ``App.js`` (both the customer app root and the
 * Supervisor Panel) so *every* call to ``confirmDialog(...)`` /
 * ``promptDialog(...)`` from ``@/lib/confirmDialog`` renders in the
 * Ponte Pos visual language instead of the OS chrome.
 *
 * The host holds a single ``request`` object at a time — since users
 * can't answer two OS dialogs simultaneously either, this is fine.
 */
export default function ConfirmDialogHost() {
  const [request, setRequest] = useState(null);
  // ``pendingRef`` guards against React strict-mode double-mount races
  // and resolves any dangling promise if a second request comes in.
  const pendingRef = useRef(null);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    const unregister = _registerConfirmHandler((req) => {
      // If there's already a pending request, cancel it first.
      if (pendingRef.current) {
        pendingRef.current.resolve(pendingRef.current.kind === "prompt" ? null : false);
      }
      pendingRef.current = req;
      setInputValue(req.opts?.defaultValue || "");
      setRequest(req);
    });
    return unregister;
  }, []);

  if (!request) return null;

  const { kind, opts } = request;
  const isPrompt = kind === "prompt";
  const isDanger = opts.variant === "danger" || opts.destructive === true;

  const finish = (val) => {
    const req = pendingRef.current;
    pendingRef.current = null;
    setRequest(null);
    setInputValue("");
    if (req) req.resolve(val);
  };

  const onCancel = () => finish(isPrompt ? null : false);
  const onConfirm = () => finish(isPrompt ? inputValue : true);

  const Icon = isPrompt ? KeyRound : isDanger ? AlertTriangle : HelpCircle;
  const iconTone = isDanger
    ? "bg-rose-100 text-rose-600"
    : isPrompt
    ? "bg-blue-100 text-blue-600"
    : "bg-amber-100 text-amber-600";

  const title = opts.title || (isPrompt ? "Απαιτούνται στοιχεία" : "Επιβεβαίωση");
  const confirmLabel = opts.confirmLabel || (isDanger ? "Διαγραφή" : "Εντάξει");
  const cancelLabel = opts.cancelLabel || "Άκυρο";

  return (
    <Dialog open={!!request} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent
        className="max-w-md z-[110]"
        data-testid="app-confirm-dialog"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (!isPrompt || inputValue.length >= 0)) {
            e.preventDefault();
            onConfirm();
          }
        }}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${iconTone}`}>
              <Icon size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-heading text-lg leading-tight" data-testid="app-confirm-title">
                {title}
              </DialogTitle>
              {opts.message && (
                <DialogDescription className="mt-1.5 whitespace-pre-line text-slate-700" data-testid="app-confirm-message">
                  {opts.message}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        {isPrompt && (
          <div className="pt-1">
            <Input
              type={opts.inputType || "text"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={opts.placeholder || ""}
              autoFocus
              className="h-11 text-base border-2"
              data-testid="app-confirm-input"
            />
          </div>
        )}

        <DialogFooter className="gap-2 pt-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={onCancel}
            className="h-10 min-w-[110px]"
            autoFocus={isDanger}
            data-testid="app-confirm-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            className={`h-10 min-w-[130px] font-bold text-white ${
              isDanger
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
            autoFocus={!isPrompt && !isDanger}
            data-testid="app-confirm-ok"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
